import React, { useState, useEffect } from "react";
import { ExportPDFButton } from "./exportCustomerPDF";
import { Box, Grid, Divider } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import swal from "sweetalert";
import {
  BusinessOutlined,
  BarChartOutlined,
  CreditCardOutlined,
  AttachFileOutlined,
} from "@mui/icons-material";
import { getRoleFromStorage } from "../../utils/auth";
import { api_path } from "../../shared.js";
import ActionButtons from "./ActionButtons";
import { updateStatus } from "../../utils/UpdateStatus.js";
import {
  PageHeader,
  SectionCard,
  FieldItem,
  MetricCard,
  FileGroup,
} from "./RegistrationShared";

export default function CustomerGeneralDetailView(props) {
  const [searchParams] = useSearchParams();
  const job_id = searchParams.get("id");
  const ROLE = getRoleFromStorage();

  const [form, setForm] = useState({});
  const [files, setFiles] = useState({});
  const [approvalStatus, setApprovalStatus] = useState(null);

  useEffect(() => {
    if (job_id) fetchCustomer(job_id);
  }, [job_id]);

  const fetchCustomer = async (id) => {
    try {
      const res = await fetch(`${api_path}/customer/get-CustomerRegistration.php?id=${id}`);
      const result = await res.json();

      // guard ป้องกัน crash ถ้า customer เป็น null
      if (!result || !result.customer) {
        console.warn("ไม่พบข้อมูล customer:", result);
        return;
      }

      const data = result.customer;

      setApprovalStatus(data.approval_status);
      setForm({
        // ── Status ──
        approval_status: data.approval_status || "",
        status_name: data.status_name || "",
        create_by: data.CreateBy || "",
        createDate: data.CreatedDate || "",

        // ── Request Info ──
        customerCode: data.CustomerCode || "",
        requestType: data.RequestType || "",
        paymentType: data.PaymentType || "",
        channel: data.Channel || "",
        agentName: data.AgentName || "",
        branch: data.CustomerBranch || "",

        // ── ข้อมูลทั่วไป ──
        nameTH: data.CustomerNameTH || "",
        nameEN: data.CustomerNameEN || "",
        taxId: data.TaxID || "",
        taxClassification: data.TaxClassification || "",

        // ที่อยู่ — รวมเป็น string เดียวสำหรับแสดงผล
        address: [
          data.CustomerAddress1,
          data.CustomerAddress2,
          data.CustomerAddress3,
          data.CustomerAddress4,
          data.CustomerSubDistrict,
          data.CustomerDistrict,
          data.CustomerCity,
          data.CustomerPostCode,
          data.Country,
        ].filter(Boolean).join(" "),

        address1: data.CustomerAddress1 || "",
        address2: data.CustomerAddress2 || "",
        address3: data.CustomerAddress3 || "",
        address4: data.CustomerAddress4 || "",
        subdistrict: data.CustomerSubDistrict || "",
        district: data.CustomerDistrict || "",
        city: data.CustomerCity || "",
        postalCode: data.CustomerPostCode || "",
        country: data.Country || "",

        tel: data.CustomerTel || "",
        ext: data.CustomerExt || "",
        fax: data.CustomerFax || "",
        mobile: data.CustomerMobile || "",
        email: data.CustomerEmail || "",
        contactPerson: data.ContactPerson || "",
        contactFinance: data.FinanceContact || "",
        businessType: data.BusinessType || "",
        registerDate: data.RegistrationDate || "",
        registeredCapital: data.RegisteredCapital || "",
        paidUpCapital: data.PaidUpCapital || "",

        // ── บัญชี ──
        reportYear: data.ReportYear || "",
        netSales: data.NetSales || "",
        netProfit: data.NetProfit || "",
        fixedAssets: data.FixedAssets || "",
        currentAssets: data.CurrentAssets || "",
        totalLiabilities: data.TotalLiabilities || "",
        shareholderEquity: data.ShareholderEquity || "",

        // ── เครดิต ──
        creditMonthlySales: data.CreditMonthlySales || "",
        creditLimitRequest: data.CreditLimit || "",
        creditTermRequest: data.CreditDay || "",
        creditCountFrom: data.CreditCountFrom || "",
        paymentMethod: data.PaymentMethod || "",
        paymentDay: data.PaymentDay || "",
      });

      setFiles(result.files || {});
    } catch (error) {
      console.error("โหลดข้อมูลไม่สำเร็จ", error);
    }
  };

  const onSubmitForm = React.useCallback(({ STATUS }) => {
    console.log("props.UserID:", props.UserID);
    console.log("localStorage USER_ID:", localStorage.getItem("USER_ID"));

    swal({ title: "Are you sure to send data?", icon: "info", dangerMode: false, buttons: true })
      .then((isConfirm) => {
        if (isConfirm) {
          updateStatus({
            JOB_ID: job_id,
            JOB_TYPE: "CUSTOMER",
            STATUS,
            REMARK: "",
            REASON_ID: "",
            REASON_NAME: "",
            USER_ID: props.UserID || JSON.parse(localStorage.getItem("USER_ID")),
          });
        }
      });
  }, [job_id, props.UserID]);

  const onEditButtonClick = React.useCallback((e) => {
    window.open(`/s-crm/CustomerRegisterForm?id=${e.row.key}&type=${e.row?.data?.SourceID}`, "_blank");
  }, []);

  const hasFiles = !!(
    files.doc_company_cert?.length ||
    files.doc_financial_statement?.length ||
    files.doc_vat20?.length ||
    files.doc_delivery_schedule?.length ||
    files.doc_company_map?.length ||
    files.doc_company_photo?.length
  );
  const formatNumber = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const num = Number(String(value).replace(/,/g, ""));
    if (isNaN(num)) return value; // ถ้าไม่ใช่ตัวเลขจริง ๆ ให้คืนค่าเดิม
    return num.toLocaleString("en-US", { maximumFractionDigits: 2 });
  };
  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <PageHeader
        title="Customer Registration Details"
        createBy={form.create_by}
        date={form.createDate}
        approvalStatus={approvalStatus}
        statusName={form.status_name}
      />

      {/* ── ข้อมูลคำขอ ── */}
      <SectionCard icon={<BusinessOutlined fontSize="small" />} title="ข้อมูลคำขอ">
        <Grid container spacing={2}>
          <FieldItem label="รหัสลูกค้า" value={form.customerCode} />
          <FieldItem label="วันที่" value={form.createDate} />
          <FieldItem label="ประเภทคำขอ" value={form.requestType} />
          <FieldItem label="ประเภทการชำระเงิน" value={form.paymentType} />
          <FieldItem label="ช่องทาง (Channel)" value={form.channel} />
          <FieldItem label="Agent Name" value={form.agentName} />
          <FieldItem label="Branch" value={form.branch} />
        </Grid>
      </SectionCard>

      {/* ── ข้อมูลลูกค้า ── */}
      <SectionCard icon={<BusinessOutlined fontSize="small" />} title="ข้อมูลลูกค้า">
        <Grid container spacing={2}>
          <FieldItem label="ชื่อลูกค้า (TH)" value={form.nameTH} xs={12} />
          <FieldItem label="ชื่อลูกค้า (EN)" value={form.nameEN} xs={12} />
          <FieldItem label="Tax ID" value={form.taxId} />
          <FieldItem label="Tax Classification" value={form.taxClassification} />
          <FieldItem label="ที่อยู่" value={form.address} xs={12} />
          <FieldItem label="โทรศัพท์" value={form.tel} />
          <FieldItem label="ต่อ" value={form.ext} />
          <FieldItem label="โทรสาร" value={form.fax} />
          <FieldItem label="มือถือ" value={form.mobile} />
          <FieldItem label="Email" value={form.email} />
          <FieldItem label="ผู้ติดต่อ" value={form.contactPerson} />
          <FieldItem label="ฝ่ายการเงิน" value={form.contactFinance} />
          <FieldItem label="ประเภทธุรกิจ" value={form.businessType} />
          <FieldItem label="วันที่จดทะเบียน" value={form.registerDate} />
          <FieldItem label="ทุนจดทะเบียน (บาท)" value={formatNumber(form.registeredCapital)} />
          <FieldItem label="ทุนชำระแล้ว (บาท)" value={formatNumber(form.paidUpCapital)} />
        </Grid>
      </SectionCard>

      {/* ── ข้อมูลด้านบัญชี ── */}
      <SectionCard icon={<BarChartOutlined fontSize="small" />} title="ข้อมูลด้านบัญชี">
        <Grid container spacing={2}>
          <FieldItem label="ปีที่รายงาน" value={form.reportYear} />
          <FieldItem label="ยอดขายสุทธิ (บาท)" value={formatNumber(form.netSales)} />
          <FieldItem label="กำไรสุทธิ (บาท)" value={formatNumber(form.netProfit)} />
          <FieldItem label="สินทรัพย์ถาวร (บาท)" value={formatNumber(form.fixedAssets)} />
          <FieldItem label="สินทรัพย์หมุนเวียน" value={formatNumber(form.currentAssets)} />
          <FieldItem label="หนี้สินรวม" value={formatNumber(form.totalLiabilities)} />
          <FieldItem label="ส่วนของผู้ถือหุ้น" value={formatNumber(form.shareholderEquity)} />
        </Grid>
      </SectionCard>

      {/* ── วงเงินเครดิต ── */}
      <SectionCard icon={<CreditCardOutlined fontSize="small" />} title="วงเงินเครดิต">
        <Grid container spacing={1.5}>
          <Grid item xs={6} sm={3}>
            <MetricCard label="วงเงินที่ขอ" value={formatNumber(form.creditLimitRequest)} unit="บาท" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <MetricCard label="จำนวนวันที่ขอ" value={form.creditTermRequest} unit="วัน" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <MetricCard label="ประมาณการยอดขาย/เดือน" value={formatNumber(form.creditMonthlySales)} unit="บาท" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <MetricCard label="กำหนดชำระทุกวันที่" value={form.paymentDay} unit="" />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <FieldItem label="นับเครดิตจาก" value={form.creditCountFrom} />
          <FieldItem label="วิธีการชำระเงิน" value={form.paymentMethod} />
        </Grid>
      </SectionCard>

      {/* ── เอกสารแนบ ── */}
      {hasFiles && (
        <SectionCard icon={<AttachFileOutlined fontSize="small" />} title="เอกสารแนบ">
          <FileGroup label="หนังสือรับรองบริษัท" files={files.doc_company_cert} />
          <FileGroup label="งบการเงิน / กำไรขาดทุน / DBD" files={files.doc_financial_statement} />
          <FileGroup label="สำเนา ภ.พ.20" files={files.doc_vat20} />
          <FileGroup label="กำหนดการรับส่งสินค้า / วางบิล / จ่ายเช็ค" files={files.doc_delivery_schedule} />
          <FileGroup label="แผนที่บริษัท / แผนที่รับเช็ค" files={files.doc_company_map} />
          <FileGroup label="ภาพถ่ายสถานประกอบการ" files={files.doc_company_photo} />
        </SectionCard>
      )}
      <ExportPDFButton form={form} files={files} />
      <Divider sx={{ mt: 2, mb: 2 }} />
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <ActionButtons
          ID={job_id}
          TYPE="CUSTOMER"
          ROLE={ROLE}
          STATUS={approvalStatus}
          onEdit={onEditButtonClick}
          onSubmit={onSubmitForm}
        />
      </Box>
    </Box>
  );
}
